import { useHistory, useParams } from "react-router-dom";
import { useT } from "@transifex/react";
import BackNavigationButton from "../../basic/backNavigationButton/BackNavigationButton";
import ExerciseCardDivider from "../../complex/onlineTest/exercises/ExerciseCardDivider";
import OnlineTestPageNavigation from "../../complex/onlineTest/layout/OnlineTestPageNavigation";
import { getStudentResultExerciseRoute, getStudentResultRoute } from "../../helpers/navigationHelper";
import useStudentResult from "../../hooks/useStudentResult";
import ContentWithHeader from "../../wrapper/ContentWithHeader/ContentWithHeader";
import LegalFooter from "../../complex/legalFooter/LegalFooter";
import StudentResultExerciseContent from "./components/StudentResultExerciseContent";
import StudentResultExerciseDescription from "./components/StudentResultExerciseDescription";
import StudentResultExerciseFrame from "./components/StudentResultExerciseFrame";
import { useStudentReviewContext } from "../../context/StudentReviewTestContext";
import { getStudentResultLoadFailure } from "../../helpers/SharedResultAccess";
import { StudentResultLoadFailures } from "../../enums/studentReview";

/**
 * The drill-in for one exercise. The ticket describes three slots (description · content · comment),
 * but the design draws each comment against its own answer, so that slot lives inside the answers —
 * a bubble below each answer (see StudentResultAnswerRow; the table's mobile card keeps it inside).
 */
const StudentResultExercisePage = () => {
    const { shareId, exerciseNumber } = useParams<{ shareId: string; exerciseNumber: string }>();
    const { result, hasLoadingError } = useStudentResult();
    const { dataLoaded } = useStudentReviewContext();

    const history = useHistory();
    const t = useT();
    const t_backToOverview = t("Back to overview", { _tags: "StudentResult" });
    const t_notFound = t("This result could not be found.", { _tags: "StudentResult" });
    const t_notLoaded = t("This result could not be loaded. Please try again.", { _tags: "StudentResult" });
    const t_exerciseNotFound = t("This exercise could not be found.", { _tags: "StudentResult" });

    const currentIndex = result.exercises.findIndex((exercise) => exercise.number === Number(exerciseNumber));
    const exercise = result.exercises[currentIndex];

    const goToExerciseAt = (index: number) => {
        const target = result.exercises[index];
        if (!target) return;
        history.push(getStudentResultExerciseRoute(shareId, target.number));
    };

    /**
     * Past the last exercise, back to the list — the result's equivalent of the online test's
     * overview, which `handleNextPage` opens there rather than dead-ending on the final page.
     */
    const goToNext = () => {
        if (currentIndex >= result.exercises.length - 1) {
            history.push(getStudentResultRoute(shareId));
            return;
        }
        goToExerciseAt(currentIndex + 1);
    };

    const renderBody = () => {
        if (hasLoadingError) {
            const isNotFound = getStudentResultLoadFailure(dataLoaded) === StudentResultLoadFailures.NOT_FOUND;
            return <p>{isNotFound ? t_notFound : t_notLoaded}</p>;
        }
        if (!exercise) {
            return <p>{t_exerciseNotFound}</p>;
        }
        return (
            <>
                <StudentResultExerciseFrame exercise={exercise}>
                    <StudentResultExerciseDescription exercise={exercise} />
                    <ExerciseCardDivider />
                    <StudentResultExerciseContent exercise={exercise} />
                </StudentResultExerciseFrame>
                <OnlineTestPageNavigation
                    currentPage={currentIndex + 1}
                    totalPages={result.exercises.length}
                    onPrevious={() => goToExerciseAt(currentIndex - 1)}
                    onNext={goToNext}
                />
            </>
        );
    };

    return (
        <ContentWithHeader
            hasGrayBackground
            footer={<LegalFooter />}
        >
            <div
                className="flex flex-1 flex-col gap-s w-full max-w-[1048px] mx-auto p-m"
                data-testid="student-result-exercise-page"
            >
                <BackNavigationButton
                    label={t_backToOverview}
                    onClick={() => history.push(getStudentResultRoute(shareId))}
                />
                {renderBody()}
            </div>
        </ContentWithHeader>
    );
};

export default StudentResultExercisePage;
